import { normalizeSectionId, buildSectionSelector, morphSection } from '@theme/section-renderer';
import { getScrollContainer } from '@theme/scroll-container';
import { RecentlyViewed } from '@theme/recently-viewed-products';
import { StandardEvents } from '@shopify/events';

const PARAMETERS = ['preview_theme_id', '_fd', 'pb', 'view'];
const ACTIVATOR = 'a[data-gb-design], a[data-gb-model], a[data-hint-plus], a[data-gb-board-switch]';
const TTL = 30 * 60 * 1000;
const RAIL_ORDER_KEY = 'gb-pdp-swatch-order';

/**
 * One delegated switch owner per document. The returned methods also permit offline testing.
 * Main markup is reconciled explicitly: morph does not descend into template.content.
 * @param {Window} window
 */
export function createPdpSwitch(window) {
  if (window.Shopify?.designMode) return;
  if (window.gbPdpSwitch) return window.gbPdpSwitch;
  const { document } = window;
  const listeners = new window.AbortController();
  const cache = new Map();
  const requests = new Map();
  const queue = [];
  const loadedModules = new Set([...document.querySelectorAll('script[type="module"][src]')].map(script => script.src));
  const timers = new Set();
  const cleanups = new Set();
  const selections = new Map();
  const images = new Map();
  let belowRequest;
  let generation = 0;
  let active;
  let queued;
  let cartRequests = 0;
  let suspended = false;
  let destroyed = false;

  const root = () => {
    const element = document.querySelector('gb-pdp');
    return element?.dataset.boardKind && !window.Shopify?.designMode ? element : null;
  };
  const timer = (callback, delay) => {
    const id = window.setTimeout(() => { timers.delete(id); callback(); }, delay);
    timers.add(id);
    return id;
  };
  const cancelTimer = id => { window.clearTimeout(id); timers.delete(id); };
  const optional = callback => {
    try { return callback(); }
    catch (error) { window.console.error('gb-pdp-switch:', error); }
  };
  const productURL = href => {
    try {
      const url = new window.URL(href, window.location.origin);
      if (url.origin !== window.location.origin || !url.pathname.includes('/products/')) return null;
      const current = new window.URL(window.location.href);
      for (const key of [...url.searchParams.keys()]) if (!PARAMETERS.includes(key)) url.searchParams.delete(key);
      for (const key of PARAMETERS) {
        if (!url.searchParams.has(key) && current.searchParams.has(key)) url.searchParams.set(key, current.searchParams.get(key));
      }
      url.hash = '';
      return url;
    } catch { return null; }
  };
  const keyFor = url => {
    const shopify = window.Shopify;
    const market = [shopify?.country, shopify?.currency?.active || root()?.dataset.currency, shopify?.locale || document.documentElement.lang].join('|');
    const query = new window.URLSearchParams(url.search);
    query.sort();
    return `${market}|${url.pathname}?${query}`;
  };
  const sectionIDs = () => {
    const main = normalizeSectionId(root().dataset.sectionId);
    const below = [];
    for (const hook of document.querySelectorAll('[data-gb-pdp-refresh], [data-gb-faq-source], [data-gb-pdp-guide-refresh]')) {
      const wrapper = hook.closest('.shopify-section');
      const id = wrapper && normalizeSectionId(wrapper.id);
      if (id && id !== main && !below.includes(id)) below.push(id);
    }
    return [main, ...below];
  };
  const primaryIDs = ids => {
    const guides = [...document.querySelectorAll('[data-gb-pdp-guide-refresh]')]
      .map(hook => hook.closest('.shopify-section'))
      .filter(Boolean)
      .map(section => normalizeSectionId(section.id));
    return ids.filter((id, index) => index === 0 || guides.includes(id));
  };
  const remember = (key, entry) => {
    cache.delete(key);
    cache.set(key, entry);
    return entry;
  };
  const cached = key => {
    const entry = cache.get(key);
    if (!entry) return;
    if (window.Date.now() - entry.fetchedAt >= TTL) { cache.delete(key); return; }
    return remember(key, entry);
  };
  const designKey = item => {
    const choice = item.querySelector('[data-gb-design]');
    return choice?.dataset.singleUrl || choice?.dataset.gbDesign;
  };
  // A slow or incompatible section response can require a full page load.
  // Carry the order only to that destination; a new PDP visit starts fresh.
  try {
    const saved = JSON.parse(window.sessionStorage.getItem(RAIL_ORDER_KEY));
    window.sessionStorage.removeItem(RAIL_ORDER_KEY);
    const rail = root()?.querySelector('[data-design-rail]');
    if (rail && saved?.path === window.location.pathname && Number.isFinite(saved.at)
      && window.Date.now() - saved.at >= 0 && window.Date.now() - saved.at <= 60000
      && Array.isArray(saved.order) && saved.order.every(key => typeof key === 'string')) {
      const items = new Map([...rail.children].map(item => [designKey(item), item]));
      for (const key of saved.order) {
        const item = items.get(key);
        if (item) { rail.append(item); items.delete(key); }
      }
      for (const item of items.values()) rail.append(item);
    }
  } catch { /* Storage can be unavailable; product navigation must still work. */ }
  const navigate = url => {
    try {
      const rail = root()?.querySelector('[data-design-rail]');
      if (rail) window.sessionStorage.setItem(RAIL_ORDER_KEY, JSON.stringify({
        path: url.pathname, at: window.Date.now(), order: [...rail.children].map(designKey),
      }));
    } catch { /* Storage can be unavailable; product navigation must still work. */ }
    window.location.assign(url.href);
  };
  const parse = html => new window.DOMParser().parseFromString(html, 'text/html');
  const part = (element, name) => element.querySelector(`[data-gb-pdp-part="${name}"]`);
  const required = ['.gb-pdp__page', 'product-component[data-product-grid-content]', '.gb-pdp__media', '.gb-pdp__info', '.gb-pdp__buy', 'product-form-component input[name="id"][ref="variantId"]', '[ref="addToCartButton"]'];

  function validate(entry, ids) {
    const bundle = JSON.parse(entry.text);
    const html = bundle[ids[0]];
    if (typeof html !== 'string' || !html.trim()) throw new Error('Missing main section');
    const doc = parse(html);
    const section = doc.getElementById(buildSectionSelector(ids[0]));
    const fresh = section?.querySelector('gb-pdp');
    const live = root();
    if (!section || section.querySelectorAll('gb-pdp').length !== 1 || !fresh?.dataset.productId || !['single', 'plus'].includes(fresh.dataset.boardKind) || !productURL(fresh.dataset.productUrl)) throw new Error('Invalid product section');
    if (live?.dataset.gbTemplateSuffix === 'blemished' && fresh.dataset.gbTemplateSuffix !== 'blemished') throw new Error('Different imperfect template');
    if (normalizeSectionId(fresh.dataset.sectionId || '') !== ids[0]) throw new Error('Different product template');
    if (!fresh.closest('.gb-section') || !live?.closest('.gb-section')) throw new Error('Missing section shell');
    if (!live || required.some(selector => !fresh.querySelector(selector) || !live.querySelector(selector))) throw new Error('Missing purchase markup');
    const form = fresh.querySelector('product-form-component');
    if (form.dataset.productId !== fresh.dataset.productId || form.querySelector('[ref="variantId"]').value !== fresh.dataset.variantId) throw new Error('Inconsistent product identity');
    if (part(live, 'designs') && !part(fresh, 'designs')) throw new Error('Missing design rail');
    const guides = primaryIDs(ids).slice(1).map(id => {
      const guideHTML = bundle[id];
      if (typeof guideHTML !== 'string' || !guideHTML.trim()) throw new Error('Missing product guide');
      const next = parse(guideHTML).getElementById(buildSectionSelector(id));
      const current = document.getElementById(buildSectionSelector(id));
      const guide = next?.querySelector('[data-gb-pdp-guide-refresh]');
      if (!current || !guide || guide.dataset.productId !== fresh.dataset.productId) throw new Error('Inconsistent product guide');
      return [current, next];
    });
    return { bundle, doc, section, fresh, guides };
  }

  function primarySlide(fresh) {
    const gallery = fresh.querySelector('gb-pdp-gallery');
    const slides = [...(gallery?.querySelectorAll('[data-slide]') || [])];
    return slides.find(node => node.dataset.mediaId && node.dataset.mediaId === gallery.dataset.variantMedia) || slides.find(node => node.dataset.slide === '0');
  }
  function primaryImage(fresh) {
    const slide = primarySlide(fresh);
    return slide?.querySelector('img') || slide?.querySelector('template')?.content.querySelector('img');
  }
  function preload(fresh) {
    const sources = [primaryImage(fresh), ...fresh.querySelectorAll('[data-gb-pdp-part="designs"] img, [data-gb-pdp-part="cards"] img')];
    for (const source of sources) {
      if (!source) continue;
      const key = ['sizes', 'srcset', 'src'].map(name => source.getAttribute(name) || '').join('|');
      if (images.has(key)) continue;
      const image = new window.Image();
      image.fetchPriority = 'low';
      image.decoding = 'async';
      images.set(key, image);
      for (const name of ['sizes', 'srcset', 'src']) if (source.hasAttribute(name)) image.setAttribute(name, source.getAttribute(name));
      optional(() => image.decode()?.catch(() => {}));
    }
  }

  function request(url, key, kind, ids) {
    const existing = requests.get(key);
    if (existing && !existing.controller.signal.aborted) {
      if (kind === 'preload' || existing.kind !== 'preload') return existing;
      // Fetch priority cannot be changed portably: restart an unfinished low-priority fetch.
      existing.controller.abort();
    }
    if (existing) requests.delete(key);
    for (const [otherKey, record] of requests) if (record.controller.signal.aborted) requests.delete(otherKey);
    if (kind !== 'preload' && requests.size >= 3) {
      const victim = [...requests.entries()].find(([, record]) => record.kind === 'preload')
        || [...requests.entries()].find(([, record]) => kind === 'activation' && record.kind !== 'activation');
      if (victim) {
        victim[1].controller.abort();
        requests.delete(victim[0]);
        queue.unshift({ url: victim[1].url, key: victim[0], kind: victim[1].kind });
      }
    }
    const controller = new window.AbortController();
    const requestURL = new window.URL(url);
    requestURL.searchParams.set('sections', primaryIDs(ids).join(','));
    const record = { controller, kind, url, promise: undefined };
    requests.set(key, record);
    const deadline = timer(() => controller.abort(), kind === 'activation' ? 4000 : 10000);
    record.promise = (async () => {
      const response = await window.fetch(requestURL.href, { signal: controller.signal, credentials: 'same-origin', priority: kind === 'preload' ? 'low' : 'high' });
      if (!response.ok) throw new Error('Section request failed');
      const entry = { text: JSON.stringify(await response.json()), fetchedAt: window.Date.now() };
      if (controller.signal.aborted) throw new Error('Aborted section request');
      let fresh;
      try { ({ fresh } = validate(entry, ids)); }
      catch { entry.navigate = true; }
      remember(key, entry);
      if (fresh) optional(() => preload(fresh));
      return entry;
    })().finally(() => {
      cancelTimer(deadline);
      if (requests.get(key) === record) requests.delete(key);
      drainPrefetch();
    });
    return record;
  }

  function drainPrefetch() {
    if (suspended || destroyed || !root()) return;
    while (queue.length && requests.size < 3) {
      const job = queue.shift();
      if (cached(job.key) || requests.has(job.key)) continue;
      request(job.url, job.key, job.kind, sectionIDs()).promise.catch(() => {});
    }
  }
  function prefetch(href, kind = 'intent') {
    if (suspended || destroyed || !root()) return;
    const url = productURL(href);
    if (!url) return;
    const key = keyFor(url);
    if (cached(key)) return;
    const index = queue.findIndex(job => job.key === key);
    if (index >= 0 && kind === 'preload') return;
    if (index >= 0) queue.splice(index, 1);
    if (kind === 'preload') {
      if (!requests.has(key)) queue.push({ url, key, kind });
      return;
    }
    if (!requests.has(key) && requests.size >= 3 && ![...requests.values()].some(record => record.kind === 'preload')) {
      queue.unshift({ url, key, kind: 'intent' });
      return;
    }
    request(url, key, 'intent', sectionIDs()).promise.catch(() => {});
  }
  function preloadAll() {
    const live = root();
    if (!live || suspended || destroyed) return;
    const model = live.dataset.boardKind === 'single' ? 'singleUrl' : 'plusUrl';
    const other = model === 'singleUrl' ? 'plusUrl' : 'singleUrl';
    prefetch(live.dataset[other], 'preload');
    if (!window.navigator.connection?.saveData) {
      const choices = [...live.querySelectorAll('[data-design-rail] [data-gb-design]')];
      const current = Math.max(0, choices.findIndex(choice => choice.closest('[data-current]')));
      const ordered = choices.map((choice, index) => ({ choice, index }))
        .sort((a, b) => Math.abs(a.index - current) - Math.abs(b.index - current) || a.index - b.index);
      for (const field of [model, other]) for (const { choice } of ordered) prefetch(choice.dataset[field], 'preload');
    }
    drainPrefetch();
  }

  function clearPending() {
    for (const [control, value] of selections) {
      if (value === null) control.removeAttribute('aria-current');
      else control.setAttribute('aria-current', value);
    }
    selections.clear();
  }
  function select(control) {
    if (!control || (!control.hasAttribute('data-gb-design') && !control.hasAttribute('data-gb-model'))) return;
    const selector = control.hasAttribute('data-gb-design') ? '[data-gb-design]' : '[data-gb-model]';
    for (const node of root().querySelectorAll(selector)) {
      selections.set(node, node.getAttribute('aria-current'));
      node.removeAttribute('aria-current');
    }
    control.setAttribute('aria-current', control.hasAttribute('data-gb-design') ? 'true' : 'page');
  }
  function cancelIntent(abortAdopted = false) {
    generation++;
    if (active?.record && (abortAdopted || active.record.kind === 'activation')) active.record.controller.abort();
    active = queued = undefined;
    clearPending();
  }
  function blocked() {
    return cartRequests > 0 || root()?.querySelector('[ref="addToCartButton"].is-loading') || root()?.querySelector('gb-pdp-float')?.busy || [...(root()?.querySelectorAll('gb-product-card') || [])].some(card => card.busy);
  }
  function drainIntent() {
    if (!queued || suspended || destroyed) return;
    if (blocked()) { timer(drainIntent, 25); return; }
    const intent = queued;
    queued = undefined;
    switchTo(intent.href, intent.options);
  }

  /** Cached main markup applies before the click handler returns; nothing waits on images or companions. */
  async function switchTo(href, options = {}) {
    const live = root();
    const url = productURL(href);
    if (!live || !url || suspended || destroyed) return;
    const key = keyFor(url);
    if (url.pathname === productURL(live.dataset.productUrl)?.pathname) { cancelIntent(true); return; }
    if (active?.key === key && !blocked()) return active.promise;
    cancelIntent();
    belowRequest?.abort();
    select(options.control);
    if (blocked()) {
      queued = { href, options };
      timer(drainIntent, 25);
      return;
    }
    const token = generation;
    const ids = sectionIDs();
    const intent = { key, record: undefined, promise: undefined };
    active = intent;
    optional(() => window.performance.mark('gb-pdp-switch:intent'));
    let deadline;
    let applied = false;
    intent.promise = (async () => {
      try {
        let entry = cached(key);
        const source = entry ? 'cache' : requests.has(key) ? 'inflight' : 'network';
        if (!entry) {
          intent.record = request(url, key, 'activation', ids);
          const timeout = new Promise((_, reject) => {
            deadline = timer(() => {
              intent.record.controller.abort();
              reject(new Error('Switch deadline'));
            }, 4000);
          });
          entry = await Promise.race([intent.record.promise, timeout]);
        }
        if (token !== generation) return;
        if (entry.navigate) { navigate(url); return; }
        optional(() => window.performance.mark('gb-pdp-switch:response'));
        let prepared;
        try { prepared = validate(entry, ids); }
        catch (error) { entry.navigate = true; throw error; }
        const { section, fresh, guides } = prepared;
        document.adoptNode(section);
        // An add can begin while the response is pending.
        if (blocked()) { queued = { href, options }; timer(drainIntent, 25); return; }
        clearPending();
        apply(live, fresh, section, guides, options, () => { applied = true; });
        optional(() => {
          window.performance.mark('gb-pdp-switch:applied');
          window.performance.measure('gb-pdp-switch', { start: 'gb-pdp-switch:intent', end: 'gb-pdp-switch:applied', detail: { source, key, imageWaitMs: 0, capHit: false, sections: ids } });
        });
        // Start companions after the synchronous main apply, guarded by the same intent generation.
        const primary = primaryIDs(ids);
        refreshBelow(productURL(live.dataset.productUrl), ids.filter(id => !primary.includes(id)), token);
      } catch (error) {
        if (applied) window.console.error('gb-pdp-switch:', error);
        else if (token === generation && !suspended && !destroyed) navigate(url);
      } finally {
        cancelTimer(deadline);
        if (active === intent) { active = undefined; if (!queued) clearPending(); }
      }
    })();
    return intent.promise;
  }

  async function refreshBelow(url, ids, token) {
    if (!ids.length || token !== generation) return;
    const controller = new window.AbortController();
    belowRequest = controller;
    const deadline = timer(() => controller.abort(), 10000);
    try {
      // Shopify accepts five sections per response. Model-specific copies can add more companions.
      for (let start = 0; start < ids.length; start += 5) {
        if (token !== generation || controller.signal.aborted || suspended || destroyed) return;
        const batch = ids.slice(start, start + 5);
        url.searchParams.set('sections', batch.join(','));
        const response = await window.fetch(url.href, { signal: controller.signal, credentials: 'same-origin', priority: 'low' });
        if (!response.ok) throw new Error('Companion request failed');
        const bundle = await response.json();
        for (const id of batch) {
          if (token !== generation || controller.signal.aborted || suspended || destroyed) return;
          const html = bundle[id];
          if (typeof html !== 'string' || !html.trim() || !parse(html).getElementById(buildSectionSelector(id))) continue;
          await morphSection(id, html, { mode: 'full', injectStylesheet: true });
        }
      }
    } catch (error) {
      if (!controller.signal.aborted && token === generation) window.console.error('gb-pdp-switch:', error);
    } finally {
      cancelTimer(deadline);
      if (belowRequest === controller) belowRequest = undefined;
    }
  }

  function copyAttributes(target, source, except = []) {
    for (const name of target.getAttributeNames()) if (!except.includes(name) && !source.hasAttribute(name)) target.removeAttribute(name);
    for (const { name, value } of source.attributes) if (!except.includes(name) && target.getAttribute(name) !== value) target.setAttribute(name, value);
  }
  /** Kept children are cursor landmarks: they are never removed or reinserted. */
  function reconcile(target, source, keep = []) {
    const pairs = keep.filter(([oldNode, newNode]) => oldNode && newNode);
    const oldNodes = [...target.childNodes].filter(node => pairs.some(([oldNode]) => oldNode === node));
    const newNodes = [...source.childNodes].filter(node => pairs.some(([, newNode]) => newNode === node));
    if (oldNodes.length !== pairs.length || newNodes.length !== pairs.length || oldNodes.some((node, i) => pairs.find(([oldNode]) => oldNode === node)?.[1] !== newNodes[i])) throw new Error('Kept part order changed');
    for (const node of [...target.childNodes]) if (!oldNodes.includes(node)) node.remove();
    let cursor = target.firstChild;
    for (const node of [...source.childNodes]) {
      const pair = pairs.find(([, newNode]) => newNode === node);
      if (pair) cursor = pair[0].nextSibling;
      else target.insertBefore(node, cursor);
    }
  }
  function syncRail(live, fresh) {
    const oldPart = part(live, 'designs'), newPart = part(fresh, 'designs');
    if (!oldPart || !newPart) return;
    copyAttributes(oldPart, newPart);
    oldPart.querySelector('.gb-pdp-designs__header')?.replaceWith(newPart.querySelector('.gb-pdp-designs__header'));
    const rail = oldPart.querySelector('[data-design-rail]');
    // Keep the entry PDP's order while updating each design in place.
    const incoming = new Map([...newPart.querySelectorAll('[role="listitem"]')].map(item => [item.querySelector('[data-gb-design]').dataset.gbDesign, item]));
    for (const item of rail.children) {
      const choice = item.querySelector('[data-gb-design]');
      const next = incoming.get(choice.dataset.gbDesign);
      if (!next) { item.hidden = true; continue; }
      incoming.delete(choice.dataset.gbDesign);
      copyAttributes(item, next, ['style']);
      const newChoice = next.querySelector('[data-gb-design]');
      if (choice.tagName === newChoice.tagName) {
        copyAttributes(choice, newChoice);
        if (choice.innerHTML !== newChoice.innerHTML) choice.replaceChildren(...newChoice.childNodes);
      } else choice.replaceWith(newChoice);
    }
    for (const item of incoming.values()) rail.append(item);
    const oldSuggestions = oldPart.querySelector('.gb-pdp-designs__suggestions');
    const newSuggestions = newPart.querySelector('.gb-pdp-designs__suggestions');
    if (oldSuggestions && newSuggestions) oldSuggestions.replaceWith(newSuggestions);
    else if (oldSuggestions) oldSuggestions.remove();
    else if (newSuggestions) rail.after(newSuggestions);
  }
  // section-renderer.js:271-282: fragment application needs this unexported style-owner contract.
  function stylesheet(section, fresh) {
    const incoming = fresh.querySelector('style[data-section-stylesheet]');
    if (!incoming) return;
    const current = section.querySelector('style[data-section-stylesheet]');
    if (current) current.textContent = incoming.textContent;
    else section.prepend(incoming);
  }
  function scripts(sources) {
    for (const old of sources) {
      if (old.type === 'module') {
        if (loadedModules.has(old.src)) continue;
        loadedModules.add(old.src);
        const script = document.createElement('script');
        script.type = 'module'; script.src = old.src;
        document.head.append(script);
      }
    }
  }
  function runtime(live, template) {
    const controller = window.GibbonStoreDiscount?.controller;
    if (controller) { controller.check(false); return; }
    if (!live.querySelector('[data-gibbon-sd]') || !template || document.getElementById('gibbon-store-discount-config')) return;
    const content = template.content;
    const config = content.querySelector('#gibbon-store-discount-config');
    if (!config) return;
    document.body.append(config.cloneNode(true));
    for (const source of content.querySelectorAll('script[src]')) {
      const script = document.createElement('script');
      for (const { name, value } of source.attributes) script.setAttribute(name, value);
      document.body.append(script);
    }
  }
  function apply(live, fresh, freshSection, guides, options, didApply) {
    const liveSection = live.closest('.shopify-section');
    const previousProductId = live.dataset.productId;
    const status = live.querySelector('[data-gb-switch-status]');
    const anchor = options.source === 'hint' ? live.querySelector('[data-gb-model="plus"]') : options.control || live.querySelector(`[data-gb-model="${options.model}"]`);
    const top = anchor?.getBoundingClientRect().top;
    const focused = document.activeElement;
    const rail = live.querySelector('[data-design-rail]');
    const scroll = getScrollContainer();
    const styles = [];
    const hold = (element, name, value) => {
      if (!element) return;
      const oldValue = element.style.getPropertyValue(name), priority = element.style.getPropertyPriority(name);
      element.style.setProperty(name, value, 'important');
      styles.push(() => oldValue ? element.style.setProperty(name, oldValue, priority) : element.style.removeProperty(name));
    };
    const left = rail?.scrollLeft;
    hold(rail, 'scroll-snap-type', 'none'); hold(rail, 'scroll-behavior', 'auto'); hold(scroll, 'overflow-anchor', 'none');
    const cleanup = () => { if (rail) rail.scrollLeft = left; for (const restore of styles) restore(); cleanups.delete(cleanup); };
    cleanups.add(cleanup);
    // A frame is cleanup only; no UI flow waits for it, including fallback navigation.
    window.requestAnimationFrame(cleanup);
    const modules = [freshSection, ...guides.map(([, next]) => next)]
      .flatMap(section => [...section.querySelectorAll('script[type="module"][src]')]);
    const runtimeTemplate = freshSection.querySelector('template[data-gb-sd-runtime]');
    const oldBadge = part(live, 'rating')?.querySelector('.jdgm-prev-badge');
    const newBadge = part(fresh, 'rating')?.querySelector('.jdgm-prev-badge');
    const keepRating = oldBadge && newBadge && ['data-average-rating', 'data-number-of-reviews'].every(name => oldBadge.getAttribute(name) === newBadge.getAttribute(name));
    const containerSelectors = ['.gb-pdp__page', 'product-component', '.gb-pdp__media', '.gb-pdp__info', '.gb-pdp__buy'];
    const containers = new Map(containerSelectors.map(selector => [selector, [live.querySelector(selector), fresh.querySelector(selector)]]));
    const get = selector => containers.get(selector);
    const oldDesigns = part(live, 'designs'), newDesigns = part(fresh, 'designs');
    const oldModels = part(live, 'models'), newModels = part(fresh, 'models');
    const oldCards = part(live, 'cards'), newCards = part(fresh, 'cards');
    const oldFloat = live.querySelector('gb-pdp-float'), newFloat = fresh.querySelector('gb-pdp-float');
    const ratingPair = [part(live, 'rating'), part(fresh, 'rating')];
    const docTitle = fresh.dataset.docTitle;
    const targetURL = productURL(fresh.dataset.productUrl);
    const detail = { url: targetURL.href, productId: fresh.dataset.productId, handle: fresh.dataset.productHandle, model: fresh.dataset.boardKind, variantId: fresh.dataset.variantId, previousProductId };
    // Decisions: carry the actual rendered Truemed node; never execute its inert incoming script.
    const widget = live.querySelector('#truemed-instructions, .truemed-instructions');
    const mount = fresh.querySelector('#truemed-instructions');
    if (widget && mount) mount.replaceWith(widget);
    for (const [current, next] of guides) {
      document.adoptNode(next);
      copyAttributes(current, next);
      current.replaceChildren(...next.childNodes);
    }
    copyAttributes(live.closest('.gb-section'), fresh.closest('.gb-section'));
    copyAttributes(live, fresh, ['aria-busy']);
    for (const pair of containers.values()) copyAttributes(...pair);
    const oldLD = liveSection.querySelector(':scope > script[type="application/ld+json"]');
    const newLD = freshSection.querySelector(':scope > script[type="application/ld+json"]');
    if (oldLD && newLD) oldLD.replaceWith(newLD);
    reconcile(...get('.gb-pdp__media'));
    syncRail(live, fresh);
    if (rail) rail.scrollLeft = left;
    if (oldModels && newModels) {
      copyAttributes(oldModels, newModels);
      if (oldCards && newCards) {
        copyAttributes(oldCards, newCards);
        for (const card of oldCards.querySelectorAll('a[data-gb-model]')) {
          const next = newCards.querySelector(`[data-gb-model="${card.dataset.gbModel}"]`);
          if (!next) throw new Error('Missing model card');
          copyAttributes(card, next); card.replaceChildren(...next.childNodes);
        }
      }
      reconcile(oldModels, newModels, [[oldCards, newCards]]);
    }
    reconcile(...get('.gb-pdp__buy'), [[oldDesigns, newDesigns], [oldModels, newModels]]);
    if (keepRating) {
      copyAttributes(...ratingPair);
      if (newBadge.hasAttribute('data-id')) oldBadge.setAttribute('data-id', newBadge.getAttribute('data-id'));
    }
    reconcile(...get('.gb-pdp__info'), [get('.gb-pdp__buy'), ...(keepRating ? [ratingPair] : [])]);
    if (oldFloat && newFloat) {
      const visible = oldFloat.classList.contains('is-visible');
      copyAttributes(oldFloat, newFloat, ['aria-hidden', 'inert', 'class']);
      oldFloat.className = newFloat.className;
      oldFloat.classList.toggle('is-visible', visible);
      oldFloat.replaceChildren(...newFloat.childNodes);
    }
    reconcile(live, fresh, [get('.gb-pdp__page'), [oldFloat, newFloat], [status, fresh.querySelector('[data-gb-switch-status]')]]);
    stylesheet(liveSection, freshSection);
    didApply();
    for (const widget of document.querySelectorAll('#judgeme_product_reviews, .jdgm-review-widget[data-entry-point]')) {
      for (const [name, value] of Object.entries({ 'data-id': detail.productId, 'data-product-id': detail.productId, 'data-product-title': live.dataset.productTitle, 'data-product-url': live.dataset.productUrl })) widget.setAttribute(name, value);
    }
    optional(() => live.refresh?.());
    optional(() => live.querySelector('gb-pdp-float')?.refresh?.());
    optional(() => scripts(modules));
    // Same external-app script contract as morph.js:592-612. Truemed is outside app blocks.
    optional(() => {
      for (const old of live.querySelectorAll('.shopify-app-block script[src]')) {
        const script = document.createElement('script');
        for (const { name, value } of old.attributes) script.setAttribute(name, value);
        old.replaceWith(script);
      }
    });
    optional(() => runtime(live, runtimeTemplate));
    if (!keepRating) optional(() => window.jdgm?.batchRenderBadges?.());
    let focus = options.source === 'hint' ? live.querySelector('[data-gb-model="plus"]') : options.source === 'choose' ? live.querySelector(`[data-gb-model="${options.model}"]`) : options.control;
    focus ||= focused?.isConnected ? focused : null;
    if (options.source !== 'choose' && focus && Number.isFinite(top)) {
      const delta = focus.getBoundingClientRect().top - top;
      if (Math.abs(delta) >= 1) scroll.scrollBy({ top: delta, behavior: 'instant' });
    }
    document.title = docTitle;
    window.history.replaceState(window.history.state, '', targetURL.href);
    focus?.focus({ preventScroll: true });
    if (options.source === 'choose' && focus) {
      const docScroll = scroll === document.scrollingElement || scroll === document.documentElement || scroll === document.body;
      const origin = docScroll ? 0 : scroll.getBoundingClientRect().top + scroll.clientTop;
      scroll.scrollTo({ top: Math.max(0, focus.getBoundingClientRect().top - origin + scroll.scrollTop - 140), behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth' });
    }
    if (status) {
      if (!status.firstChild) status.append(document.createTextNode(''));
      status.firstChild.data = live.querySelector('h1')?.textContent.trim() || '';
    }
    clearPending();
    optional(() => RecentlyViewed.addProduct(detail.productId));
    optional(() => live.querySelector('product-component')?.dispatchViewEvent?.());
    document.dispatchEvent(new window.CustomEvent('gb:product-switch', { bubbles: true, detail }));
  }

  function controlFor(event) {
    if (!(event.target instanceof window.Element)) return;
    const control = event.target.closest(ACTIVATOR);
    const live = root();
    if (!control || !live || (!live.contains(control) && !control.hasAttribute('data-gb-board-switch')) || control.getAttribute('aria-disabled') === 'true' || control.hasAttribute('download') || (control.target && control.target !== '_self') || !productURL(control.href)) return;
    return control;
  }
  function activate(event, control) {
    if (event.defaultPrevented || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
    if (event.type === 'click' && event.button !== 0) return;
    if (control.hasAttribute('data-gb-board-switch')) return;
    event.preventDefault();
    event.stopImmediatePropagation();
    if ((selections.has(control) ? selections.get(control) !== null : control.hasAttribute('aria-current'))) {
      if (active || queued) cancelIntent(true);
      return;
    }
    switchTo(control.href, { control, source: control.hasAttribute('data-gb-design') ? 'swatch' : control.hasAttribute('data-gb-model') ? 'card' : 'hint' });
  }
  const on = (target, type, handler, extra = {}) => target.addEventListener(type, handler, { signal: listeners.signal, ...extra });
  on(document, 'click', event => { const control = controlFor(event); if (control) activate(event, control); }, { capture: true });
  on(document, 'keydown', event => {
    const control = controlFor(event);
    if (event.key === ' ' && !event.repeat && control?.hasAttribute('data-gb-model')) activate(event, control);
  }, { capture: true });
  on(document, 'gb:board-switch-request', event => {
    const live = root(), url = productURL(event.detail?.url);
    if (!live || !url || event.defaultPrevented || !['single', 'double'].includes(event.detail?.model) || url.pathname === productURL(live.dataset.productUrl)?.pathname) return;
    event.preventDefault();
    switchTo(url.href, { source: 'choose', model: event.detail.model === 'double' ? 'plus' : 'single' });
  });
  on(document, 'focusin', event => { const control = controlFor(event); if (control) prefetch(control.href); }, { capture: true });
  on(document, 'touchstart', event => { const control = controlFor(event); if (control) prefetch(control.href); }, { capture: true, passive: true });
  on(document, 'pointerdown', event => { const control = controlFor(event); if (control && ['touch', 'pen'].includes(event.pointerType)) prefetch(control.href); }, { capture: true, passive: true });
  on(document, StandardEvents.cartLinesUpdate, async event => {
    if (event.target !== root()?.querySelector('product-form-component')) return;
    cartRequests++;
    try { await event.promise; } catch { /* Main form owns the error. */ }
    finally { cartRequests--; drainIntent(); }
  });
  function reset() {
    generation++;
    active = queued = undefined;
    for (const record of requests.values()) record.controller.abort();
    requests.clear(); queue.length = 0;
    for (const id of timers) window.clearTimeout(id);
    timers.clear();
    belowRequest?.abort();
    belowRequest = undefined;
    for (const cleanup of [...cleanups]) cleanup();
    clearPending();
  }
  on(window, 'pagehide', () => { suspended = true; reset(); });
  on(window, 'pageshow', event => { if (event.persisted) { suspended = false; reset(); preloadAll(); } });
  if (document.readyState === 'complete') preloadAll();
  else on(window, 'load', () => preloadAll(), { once: true });
  const controller = { switchTo, prefetch, destroy() { destroyed = true; reset(); listeners.abort(); delete window.gbPdpSwitch; } };
  window.gbPdpSwitch = controller;
  return controller;
}

if (typeof window !== 'undefined') createPdpSwitch(window);
